import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Book, Gamepad2, Star, Palette, Trophy, Map, Store,
  Sparkles, Flame, ChevronRight, Zap, Clock, Users, Shield,
  BookOpen, Target, Award, Wand2, Heart, Pen, Quote, UserPlus, Rocket, GraduationCap,
  Brain, BarChart3, ArrowRight
} from "lucide-react";
import { useApp, API, IMG, hasAuthToken } from "../context";
import { isAdminUser } from "../utils/adminCheck";
import { AuthGate } from "../components/shared";
import PremiumUpgradeModal from "../components/PremiumUpgradeModal";
import {
  DayNightCycle, ParallaxStars, ForegroundSparkles,
  ShootingStars, PortalTransition
} from "../components/shared";
import ThemeSpecialEffects from "../components/ThemeSpecialEffects";
import InteractiveSkyGame from "../components/InteractiveSkyGame";
import WeatherOverlay from "../components/WeatherOverlay";
import NighttimeEnhancements from "../components/NighttimeEnhancements";
import WelcomeBackSequence from "../components/WelcomeBackSequence";

import DailyStarWheel from "../components/DailyStarWheel";
import LevelInfoModal from "../components/LevelInfoModal";
import ProfileCompletionNudge from "../components/ProfileCompletionNudge";
import SetPasswordNudge from "../components/SetPasswordNudge";
import CompleteProfileTip from "../components/CompleteProfileTip";
import AddExplorerTip from "../components/AddExplorerTip";
import DueSoonReminder from "../components/DueSoonReminder";
import { toast } from "sonner";
import { useIsaiah, getWorldPose } from "../components/isaiah";
import { useABTest } from "../hooks/useABTest";
import { usePremiumAccess } from "../hooks/usePremiumAccess";
import { startGuestRoam, isGuestRoamActive, guestRoamRemainingMs } from "../components/PrelaunchGate";
import {
  MouseSparkleTrail, AnimatedOrbs, LightRays, WaveRibbons, FloatingPlanets,
  BoldSparkles, HorizonGlow, PulsingRings, GradientMesh, MeteorTrails,
  GiantComet, Nebulas, Fireflies, CloudLayers, ConstellationPaths
} from "./home/HomeEffects";
import {
  TestimonialsCarousel, useSkyTheme, WORLD_GUIDES, MAIN_WORLDS, UTILITY_NODES,
  useNewArrivals, HORIZON_ISLANDS, WorldMapNode, HorizonMysteryNode,
  HOMEPAGE_MESSAGES, IsaiahHeroPresence, getGreeting, AdventurePortal,
  TIER_PALETTES, WorldEvolutionLayer
} from "./home/HomeWorldMap";
import { getAppTheme, getAppThemeConfig, APP_THEME_EVENT } from "../lib/appTheme";

const HomePage = () => {
  const { user, sounds } = useApp();
  const navigate = useNavigate();
  const sky = useSkyTheme();
  const { setCompanionPose } = useIsaiah();
  const [portalFx, setPortalFx] = useState(null);
  const [streak, setStreak] = useState(null);
  const [progress, setProgress] = useState(null);
  const [levelInfoOpen, setLevelInfoOpen] = useState(false);
  const [featuredPath, setFeaturedPath] = useState(null);
  const [events, setEvents] = useState([]);
  const [adventurePaths, setAdventurePaths] = useState([]);
  const [dailyChallenges, setDailyChallenges] = useState(null);
  const [skyTheme, setSkyTheme] = useState(null);
  const [weatherEvent, setWeatherEvent] = useState(null);
  const [seasonalEvent, setSeasonalEvent] = useState(null);
  const [multiplayerEvent, setMultiplayerEvent] = useState(null);
  const [drawingOfDay, setDrawingOfDay] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [evolution, setEvolution] = useState(null);
  const [hoveredWorld, setHoveredWorld] = useState(null);
  const [mouseY, setMouseY] = useState(0);
  const [newArrivalsCount, setNewArrivalsRef] = useNewArrivals();
  // Premium gate for the Homepage AI Tutor Spotlight, mirrors the LearnPage tile.
  const [premiumModal, setPremiumModal] = useState(null);
  const [showGate, setShowGate] = useState(false);
  const isPremium = usePremiumAccess(user).premium;

  // Personal background theme (the Palette picker in the top bar). A named
  // theme is the user's EXPLICIT choice, so it OVERRIDES both the time-of-day
  // sky and any server-driven sky theme — no more auto-cycling once picked.
  const [appTheme, setAppThemeState] = useState(getAppTheme);
  useEffect(() => {
    const onTheme = (e) => setAppThemeState(e?.detail || getAppTheme());
    const relearn = () => setAppThemeState(getAppTheme());
    window.addEventListener(APP_THEME_EVENT, onTheme);
    window.addEventListener("active-learner-changed", relearn);
    return () => {
      window.removeEventListener(APP_THEME_EVENT, onTheme);
      window.removeEventListener("active-learner-changed", relearn);
    };
  }, []);

  // Insider Founder welcome, when the visitor came via the secret /home
  // entry URL (we set `iw_insider_intent` in localStorage there) AND they
  // aren't signed in yet, show a friendly gold pill in the hero so they
  // know they're meant to sign up as a Founder, not a regular visitor.
  const [isInsiderFounder, setIsInsiderFounder] = useState(false);
  useEffect(() => {
    try { setIsInsiderFounder(localStorage.getItem("iw_insider_intent") === "1"); } catch (_) {}
  }, [user]);

  // A/B Test hooks - only active when experiments are running
  const heroCta = useABTest("hero-cta");
  const heroHeadline = useABTest("hero-headline");
  const finalCta = useABTest("final-cta");

  useEffect(() => {
    // Bundle of read-only world-state calls, one round-trip instead of 7.
    // The backend fans out to the same handlers concurrently, so any one
    // sub-call failure doesn't block the rest.
    axios.get(`${API}/home/initial`).then(r => {
      const d = r.data || {};
      if (d.world_events) setEvents(d.world_events.events || d.world_events || []);
      if (d.sky_theme) setSkyTheme(d.sky_theme);
      if (d.sky_multiplayer) setMultiplayerEvent(d.sky_multiplayer);
      // (sky_collectibles, theme_event, nighttime, weather are consumed by
      // child components that subscribe to the same APIs lazily , the
      // bundled fetch warms the server cache for those children too.)
    }).catch(() => {});
    // Endpoints not in the bundle (different feature areas), still individual.
    axios.get(`${API}/storyteller/paths`).then(r => setAdventurePaths((r.data.paths || []).slice(0, 3))).catch(() => {});
    axios.get(`${API}/challenges/daily`).then(r => setDailyChallenges(r.data.challenges)).catch(() => {});
    axios.get(`${API}/weather/current`).then(r => { if (r.data.has_event) setWeatherEvent(r.data); }).catch(() => {});
    axios.get(`${API}/seasonal/current`).then(r => { if (r.data.has_event) setSeasonalEvent(r.data.event); }).catch(() => {});
    axios.get(`${API}/art-studio/drawing-of-the-day`).then(r => { if (r.data.drawing) setDrawingOfDay(r.data); }).catch(() => {});
    if (user) {
      const uid = user.id || user.email;
      axios.get(`${API}/social/streak/${uid}`).then(r => setStreak(r.data)).catch(() => {});
      axios.get(`${API}/explorer/progress/${uid}`).then(r => setProgress(r.data)).catch(() => {});
      axios.get(`${API}/learning-path/${uid}`).then(r => setFeaturedPath(r.data)).catch(() => {});
      axios.get(`${API}/world-evolution/state/${uid}`).then(r => setEvolution(r.data)).catch(() => {});
      axios.post(`${API}/social/streak/check-in`, { user_id: uid }).catch(() => {});
    }
    // Auto-play world music on home
    if (sounds?.musicEnabled) sounds.playWorldMusic?.("home");
  }, [user]);

  // Subtle parallax: track mouse Y position
  useEffect(() => {
    const handler = (e) => {
      setMouseY((e.clientY / window.innerHeight - 0.5) * 2); // -1 to 1
    };
    window.addEventListener("mousemove", handler, { passive: true });
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  // Best Streak Confetti, celebrate the moment a child beats their record.
  useEffect(() => {
    if (!user || !streak) return;
    const best = streak.longest_streak || 0;
    if (best < 2 || (streak.current_streak || 0) < best) return;
    const key = `beststreak_celebrated_${user.id || user.email}`;
    let prev = 0;
    try { prev = parseInt(localStorage.getItem(key) || "0", 10) || 0; } catch { prev = 0; }
    if (best > prev) {
      try { localStorage.setItem(key, String(best)); } catch { /* ignore */ }
      import("canvas-confetti").then((m) => {
        m.default({ particleCount: 120, spread: 80, origin: { y: 0.35 }, colors: ["#FBBF24", "#F59E0B", "#FB923C", "#FCD34D"] });
      }).catch(() => {});
    }
  }, [user, streak]);


  const enterWorld = (to, color) => {
    if (sounds) sounds.playSound("pop");
    setPortalFx(color);
    setTimeout(() => navigate(to), 400);
  };

  const isNight = sky.period === "night" || sky.period === "dusk";
  // A named personal theme wins over both time-of-day and the server sky theme.
  const appThemeCfg = getAppThemeConfig(appTheme);
  const usingNamedTheme = appTheme !== "auto" && !!appThemeCfg.sky;
  const effIsNight = usingNamedTheme ? true : isNight;
  const activeGradient = usingNamedTheme
    ? appThemeCfg.sky
    : (skyTheme ? (isNight ? skyTheme.gradient_night : skyTheme.gradient_day) : sky.sky);
  const effStarOpacity = usingNamedTheme ? appThemeCfg.starOpacity : sky.starOpacity;
  const effOrbColors = usingNamedTheme ? appThemeCfg.orbColors : skyTheme?.orb_colors;
  const cloudOpacity = usingNamedTheme ? 0.08 : (skyTheme?.cloud_opacity ?? 0.2);

  // Track A/B impressions on mount
  useEffect(() => {
    heroCta.trackImpression();
    heroHeadline.trackImpression();
    finalCta.trackImpression();
  }, [heroCta.variant, heroHeadline.variant, finalCta.variant]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="living-world" data-testid="homepage">
      {showWelcome && <WelcomeBackSequence onComplete={() => setShowWelcome(false)} />}
      <MouseSparkleTrail />

      {/* ---- LAYER 1: Far cosmic sky ---- */}
      <div className={`absolute inset-0 bg-gradient-to-b ${activeGradient} transition-colors duration-[10000ms]`} />
      <DayNightCycle />

      {/* ---- LAYER 2: Constellations / distant stars (parallax: slow) ---- */}
      <div style={{ transform: `translateY(${mouseY * -2}px)`, transition: "transform 0.3s ease-out" }}>
        <GradientMesh />
        {effStarOpacity > 0 && <ParallaxStars opacity={effStarOpacity} />}
        <Nebulas />
      </div>

      {/* ---- LAYER 3: Midfield aurora / atmosphere (parallax: medium) ---- */}
      <div style={{ transform: `translateY(${mouseY * -5}px)`, transition: "transform 0.3s ease-out" }}>
        <AnimatedOrbs orbColors={effOrbColors} />
        <WaveRibbons />
        <LightRays />
        <PulsingRings />
        <HorizonGlow color={usingNamedTheme ? undefined : skyTheme?.horizon_color} />
        {!usingNamedTheme && skyTheme?.special && <ThemeSpecialEffects themeSpecial={skyTheme.special} />}
      </div>

      {/* ---- LAYER 3.5: Floating Planets (own layer to avoid collapsing parent) ---- */}
      <FloatingPlanets showPlanets={skyTheme?.show_planets} />

      {/* ---- LAYER 4: World evolution (progress-reactive) ---- */}
      <WorldEvolutionLayer evolution={evolution} />

      {/* ---- LAYER 5: Foreground sparkles / meteors (parallax: fast) ---- */}
      <div style={{ transform: `translateY(${mouseY * -8}px)`, transition: "transform 0.3s ease-out" }}>
        <MeteorTrails />
        <BoldSparkles isNight={effIsNight} sparkleColors={usingNamedTheme ? appThemeCfg.orbColors : skyTheme?.sparkle_colors} />
        <ShootingStars />
        {effIsNight && <Fireflies count={15} />}
        <ForegroundSparkles />
      </div>
      <GiantComet />
      <CloudLayers opacity={cloudOpacity} />
      <WeatherOverlay />
      <NighttimeEnhancements isNight={effIsNight} />
      <InteractiveSkyGame />

      {/* ---- HERO SECTION ---- */}
      <section className="relative z-10 pt-12 md:pt-16 pb-4 text-center">
        <div className="relative max-w-4xl mx-auto px-4">
          {/* Founder welcome pill, surfaces ONLY for /home insider visitors
              who haven't signed up yet. Quietly tells personal contacts
              "you're meant to sign up, not browse as a guest." */}
          {!user && isInsiderFounder && (
            <div className="flex justify-center mb-4" data-testid="founder-welcome-pill">
              <button
                onClick={() => navigate("/signup")}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-amber-100 text-xs sm:text-sm font-fredoka font-bold border border-amber-400/50 bg-gradient-to-r from-amber-500/20 via-yellow-500/15 to-amber-500/20 hover:from-amber-500/30 hover:to-amber-500/30 transition-all shadow-lg shadow-amber-500/10"
                data-testid="founder-welcome-cta"
              >
                <span className="text-amber-300">👋</span>
                Welcome, Founder, sign up to claim your spot
                <span className="text-amber-300">→</span>
              </button>
            </div>
          )}
          {/* Isaiah Hero Presence - replaces generic avatar */}
          <div className="flex justify-center mb-4" data-testid="isaiah-avatar">
            <IsaiahHeroPresence
              user={user}
              streak={streak}
              progress={progress}
              hoveredWorld={hoveredWorld}
            />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-fredoka font-bold tracking-tight">
            <span style={{ backgroundImage: "linear-gradient(90deg, #C4B5FD, #FBCFE8, #FDE68A, #C4B5FD)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "title-shimmer 6s linear infinite" }}>Isaiah's</span>{" "}
            <span style={{ backgroundImage: "linear-gradient(90deg, #4ADE80, #FBBF24, #FB923C, #4ADE80)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "title-shimmer 8s linear infinite" }}>World</span>
          </h1>
          <p className="text-white/80 text-base sm:text-lg mt-2 font-nunito font-bold max-w-lg mx-auto" data-testid="hero-benefit-headline">
            {heroHeadline.variantValue || "Where Kids, Parents & Solo Explorers of All Ages Learn, Play & Create"}
          </p>
          <p className="text-white/45 text-xs sm:text-sm mt-1.5 font-nunito max-w-md mx-auto">
            {usingNamedTheme ? `${appThemeCfg.label} — ${appThemeCfg.desc}.` :
             sky.period === "night" ? "The stars are out! A perfect night for adventure." :
             sky.period === "dawn" ? "A new day dawns across the islands!" :
             sky.period === "dusk" ? "Golden hour magic fills the sky." :
             "A beautiful day to explore the universe!"}
          </p>

          {user && progress && (
            <button onClick={() => setLevelInfoOpen(true)}
              className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 mt-4 hover:bg-white/10 transition-colors cursor-pointer" data-testid="explorer-hud">
              <span className="text-white text-xs font-bold">Lv.{progress.level || 1}</span>
              <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-400 to-purple-400 rounded-full transition-all" style={{ width: `${Math.min(100, progress.level_progress_pct != null ? progress.level_progress_pct : ((progress.xp || 0) % 100))}%` }} />
              </div>
              <span className="flex items-center gap-1 text-yellow-300 text-xs"><Star size={10} />{progress.stars || 0}</span>
              {streak?.current_streak > 0 && <span className="flex items-center gap-1 text-orange-400 text-xs"><Flame size={10} />{streak.current_streak}d</span>}
              {evolution && evolution.tier >= 2 && (
                <span className="flex items-center gap-1 text-[10px] font-bold" style={{ color: evolution.tier >= 4 ? "#FBBF24" : "#C4B5FD" }}>
                  {evolution.tier >= 5 ? "👑" : evolution.tier >= 4 ? "⚡" : evolution.tier >= 3 ? "🌟" : "🔮"} {evolution.tier_name}
                </span>
              )}
              <span className="text-white/30 text-[11px] font-bold ml-0.5">?</span>
            </button>
          )}
          <LevelInfoModal open={levelInfoOpen} onClose={() => setLevelInfoOpen(false)} current={progress} />

          {user && streak?.longest_streak > 0 && (
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-2xl bg-gradient-to-r from-amber-500/15 to-orange-500/15 border border-amber-400/30 mt-3" data-testid="streak-recap">
              <Trophy size={18} className="text-amber-300" />
              <div className="text-left leading-tight">
                <p className="text-amber-200 text-[10px] font-bold uppercase tracking-wide">Best Streak</p>
                <p className="text-white text-sm font-fredoka font-bold">{streak.longest_streak} day{streak.longest_streak === 1 ? "" : "s"} 🔥</p>
              </div>
            </div>
          )}

          {!user && (
            <div className="flex flex-col items-center gap-3 mt-4">
              <button onClick={() => { heroCta.trackConversion(); navigate("/signup"); }} className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-fredoka font-bold text-sm transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(139,92,246,0.4)]" style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6, #EC4899)", boxShadow: "0 4px 20px rgba(139,92,246,0.3)" }} data-testid="get-started-hero">
                <Sparkles size={16} /> {heroCta.variantValue || "Start Your Adventure"}
              </button>
              <div className="flex items-center gap-4">
                <button onClick={() => navigate("/login")} className="text-white/50 hover:text-white text-sm transition-colors" data-testid="hero-login-link">
                  Already have an account? Log in
                </button>
              </div>
              {/* Solo Explorer path, teens 13+, adults, homeschoolers who
                  don't need a parent/child setup. Short, understated CTA so it
                  doesn't steal focus from the main Start button above. */}
              <button
                onClick={() => navigate("/signup?role=solo_explorer")}
                className="mt-1 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-fuchsia-500/10 hover:bg-fuchsia-500/20 border border-fuchsia-400/30 hover:border-fuchsia-300/60 text-fuchsia-200 hover:text-white text-xs font-nunito font-bold transition-all"
                data-testid="hero-solo-explorer-cta"
              >
                <span>Using this for yourself? Start as a solo Explorer, no family setup required.</span>
                <span aria-hidden>→</span>
              </button>
            </div>
          )}

          {/* Trust Signals */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mt-6 px-4" data-testid="hero-trust-signals">
            {[
              { value: "5,000+", label: "Families", icon: Users },
              { value: "100K+", label: "Games Played", icon: Gamepad2 },
              { value: "4.9", label: "Rating", icon: Star, isStar: true },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                {i > 0 && <div className="w-px h-4 bg-white/12 hidden sm:block" />}
                <s.icon size={14} className={s.isStar ? "text-amber-400 fill-amber-400" : "text-white/35"} />
                <span className="text-white/80 text-xs font-fredoka font-bold">{s.value}</span>
                <span className="text-white/35 text-[10px] font-nunito">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- WORLD EVENT BANNER ---- */}
      {events.length > 0 && (
        <section className="relative z-10 px-4 mb-2" style={{ animation: "section-rise 1s ease-out 0.2s both" }}>
          <div className="max-w-3xl mx-auto">
            {events.slice(0, 1).map(evt => (
              <div key={evt.id || evt.name} onClick={() => navigate("/events")} className="cursor-pointer rounded-2xl border border-yellow-500/20 bg-yellow-500/5 p-3 flex items-center gap-3 hover:bg-yellow-500/10 transition-all hover:scale-[1.01]" data-testid="world-event-banner">
                <span className="text-2xl animate-pulse">{evt.icon || "🌟"}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-yellow-300 text-xs font-bold">{evt.title || evt.name || "World Event"}</p>
                  <p className="text-white/50 text-xs truncate">{evt.description || "Something magical is happening!"}</p>
                </div>
                <ChevronRight size={14} className="text-yellow-500/50" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- DAILY STAR WHEEL (guests + logged-in explorers) ---- */}
      {user && (
        <section className="relative z-10 px-4 py-3" style={{ animation: "section-rise 1s ease-out 0.25s both" }} data-testid="home-daily-wheel-section">
          <div className="max-w-3xl mx-auto space-y-3">
            <DueSoonReminder />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start" data-testid="home-onboarding-cards">
              <CompleteProfileTip />
              <AddExplorerTip />
            </div>
            <SetPasswordNudge />
            <ProfileCompletionNudge />
            <DailyStarWheel />
          </div>
        </section>
      )}

      {/* ======== SPATIAL WORLD MAP ======== */}
      <section className="relative z-10 px-4 py-4" style={{ animation: "section-rise 1s ease-out 0.3s both" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-3">
            <h2 className="text-base md:text-lg font-fredoka font-bold text-white/70 flex items-center justify-center gap-2" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
              <Map size={18} className="text-blue-400" /> Isaiah's Worlds
            </h2>
          </div>

          {/* Main Worlds Map - Mobile Grid */}
          <div className="md:hidden grid grid-cols-2 gap-3" data-testid="world-map-mobile">
            {MAIN_WORLDS.map((w) => {
              const WIcon = w.icon;
              const isComingSoon = w.comingSoon === true;
              return (
                <div key={w.id} onClick={() => !isComingSoon && enterWorld(w.to, w.color)}
                  onMouseEnter={() => setHoveredWorld(w.id)}
                  onMouseLeave={() => setHoveredWorld(null)}
                  className={`group rounded-2xl border-2 p-4 flex flex-col items-center text-center transition-all duration-300 active:scale-95 hover:scale-[1.04] relative overflow-hidden ${isComingSoon ? "cursor-default" : "cursor-pointer"}`}
                  style={{ borderColor: `${w.color}50`, background: `linear-gradient(145deg, ${w.color}15, rgba(11,17,32,0.85))`, boxShadow: `0 0 15px ${w.color}15, 0 4px 16px rgba(0,0,0,0.3)` }}
                  data-testid={`world-node-${w.id}`}>
                  {isComingSoon && (
                    <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden rounded-xl">
                      <div className="absolute w-[200%] text-center py-0.5 font-fredoka font-bold text-[7px] tracking-[0.12em] uppercase"
                        style={{ background: "linear-gradient(135deg, #F97316, #FB923C)", color: "#fff", transform: "rotate(-35deg)", top: "38%", left: "-50%", textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>
                        Coming Soon &nbsp;&middot;&nbsp; Coming Soon
                      </div>
                    </div>
                  )}
                  <div className={`mb-1.5 ${isComingSoon ? "opacity-40" : ""}`} style={{ filter: `drop-shadow(0 0 8px ${w.color})` }}>
                    <WIcon size={30} style={{ color: w.color }} />
                  </div>
                  <span className={`text-xs font-fredoka font-bold ${isComingSoon ? "text-white/40" : "text-white"}`}>{w.name}</span>
                  <span className="text-white/50 text-[10px] mt-0.5">{isComingSoon ? "Coming soon!" : w.desc}</span>
                  {!isComingSoon && w.guide && (
                    <p className="text-[9px] mt-1.5 italic leading-snug" style={{ color: `${w.color}90` }}>
                      {w.guide.emoji} "{w.guide.lines[0]}"
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Main Worlds Map - Desktop Spatial (4 primary destinations) */}
          <div className="hidden md:block relative w-full rounded-3xl overflow-hidden border border-white/5" style={{ aspectRatio: "16/5.5", minHeight: "200px" }} data-testid="world-map">
            {/* Map atmosphere */}
            <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 70% at 50% 35%, rgba(139,92,246,0.06), transparent)" }} />

            {/* Constellation paths connecting main worlds */}
            <ConstellationPaths />

            {/* Main world nodes (prominent) */}
            {MAIN_WORLDS.map((w, i) => (
              <WorldMapNode key={w.id} world={w} index={i} progress={progress} onClick={() => enterWorld(w.to, w.color)} onHover={setHoveredWorld} />
            ))}

            {/* Ambient particles */}
            {Array.from({ length: 15 }, (_, i) => (
              <div key={`mp-${i}`} className="absolute w-0.5 h-0.5 rounded-full bg-white/20 pointer-events-none" style={{
                left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
                animation: `star-pulse ${3 + Math.random() * 4}s ease-in-out ${Math.random() * 5}s infinite`,
              }} />
            ))}
          </div>

          {/* Utility Nodes Dock (secondary destinations) */}
          <div className="mt-3" data-testid="utility-dock">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px flex-1 max-w-[100px] bg-gradient-to-r from-transparent to-white/25" />
              <span className="text-white/90 text-sm font-fredoka font-bold tracking-widest uppercase" style={{ textShadow: "0 0 12px rgba(255,255,255,0.2)" }}>Explorer Tools</span>
              <div className="h-px flex-1 max-w-[100px] bg-gradient-to-l from-transparent to-white/25" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-w-2xl mx-auto">
              {UTILITY_NODES.map((node) => {
                const NodeIcon = node.icon;
                const isMarketplace = node.id === "marketplace";
                const blocked = false; // Marketplace is fully live

                return (
                  <div key={node.id}
                    ref={isMarketplace ? setNewArrivalsRef : undefined}
                    onClick={() => {
                      if (isMarketplace) localStorage.setItem("marketplace_last_visit", new Date().toISOString());
                      enterWorld(node.to, node.color);
                    }}
                    className="group cursor-pointer rounded-xl border-2 p-3 sm:p-4 flex flex-col items-center text-center hover:scale-[1.04] transition-all relative overflow-hidden"
                    style={{ borderColor: node.color, background: `linear-gradient(135deg, ${node.color}10, rgba(255,255,255,0.02))`, boxShadow: `0 4px 15px ${node.color}15` }}
                    data-testid={`utility-${node.id}`}>
                    {isMarketplace && newArrivalsCount > 0 && !blocked && (
                      <div className="absolute -top-1 -right-1 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow-lg animate-pulse"
                        style={{ background: "linear-gradient(135deg, #F97316, #EF4444)", boxShadow: "0 2px 8px rgba(249,115,22,0.5)" }}
                        data-testid="marketplace-new-badge">
                        {newArrivalsCount} New
                      </div>
                    )}
                    {blocked && (
                      <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden rounded-xl">
                        <div className="absolute w-[200%] text-center py-1 font-fredoka font-bold text-[10px] tracking-[0.15em] uppercase"
                          style={{ background: "linear-gradient(135deg, #F97316, #FB923C)", color: "#fff", transform: "rotate(-35deg)", top: "38%", left: "-50%", textShadow: "0 1px 3px rgba(0,0,0,0.3)", boxShadow: "0 2px 12px rgba(249,115,22,0.35)" }}>
                          Coming Soon &nbsp;&middot;&nbsp; Coming Soon &nbsp;&middot;&nbsp; Coming Soon
                        </div>
                      </div>
                    )}
                    <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center mb-1.5 sm:mb-2 transition-transform group-hover:scale-110 ${blocked ? "opacity-40" : ""}`} style={{ background: `${node.color}20`, border: `2px solid ${node.color}40` }}>
                      <NodeIcon size={20} className="sm:hidden" style={{ color: node.color }} />
                      <NodeIcon size={26} className="hidden sm:block" style={{ color: node.color }} />
                    </div>
                    <span className={`text-xs sm:text-sm font-fredoka font-bold group-hover:text-white transition-colors ${blocked ? "text-white/40" : "text-white/90"}`}>{node.name}</span>
                    <span className={`text-[10px] sm:text-xs mt-0.5 font-semibold ${blocked ? "text-white/25" : "text-white/60"}`}>{blocked ? "Coming soon!" : node.desc}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Horizon Worlds - HIDDEN from homepage (documented in Admin Dashboard) */}
          {/* Distant Horizons section hidden for launch - managed via Admin > Hidden Sections */}
        </div>
      </section>

      {/* Isaiah hero presence is now integrated in the hero section above */}

      {/* ---- FEATURES / BENEFITS SECTION ---- */}
      <section className="relative z-10 px-4 py-6" data-testid="features-benefits">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-center text-white font-fredoka font-bold text-lg sm:text-xl mb-5 flex items-center justify-center gap-2">
            <Zap size={18} className="text-amber-300" /> Why Families Choose Isaiah's World
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Brain, title: "AI-Powered Learning", desc: "Lessons, tutoring, and activities that adapt to your child's level in real time. Powered by GPT so every interaction is personalized.", color: "#A78BFA", border: "border-purple-400/30" },
              { icon: Shield, title: "100% Kid-Safe & Ad-Free", desc: "A walled-garden universe with AI content filtering, no ads, no external links. Parents have full visibility into every activity.", color: "#4ADE80", border: "border-emerald-400/30" },
              { icon: BarChart3, title: "Track Progress Easily", desc: "See what your child is learning, how they're growing, and where they shine. Weekly reports and a full game history dashboard.", color: "#60A5FA", border: "border-blue-400/30" },
            ].map((f, i) => (
              <div
                key={i}
                className={`p-5 rounded-2xl border ${f.border} hover:scale-[1.02] transition-all duration-300`}
                style={{
                  background: "rgba(30, 41, 59, 0.35)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <div className="w-12 h-12 rounded-xl mb-3 flex items-center justify-center" style={{ background: `${f.color}20`, border: `1px solid ${f.color}40` }}>
                  <f.icon size={22} style={{ color: f.color }} />
                </div>
                <h3 className="text-white font-fredoka font-bold text-base mb-1.5" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{f.title}</h3>
                <p className="text-white/70 text-xs leading-relaxed font-nunito">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- SEASONAL / WEATHER BANNERS ---- */}
      <div className="relative z-10 px-4 space-y-2">
        <div className="max-w-3xl mx-auto space-y-2">
          {seasonalEvent && (
            <div className={`rounded-2xl border border-green-500/30 bg-green-500/10 p-3 flex items-center gap-3 ${seasonalEvent.special_css || ''}`} data-testid="seasonal-banner">
              <span className="text-xl">🎉</span>
              <div className="flex-1"><p className="text-green-300 text-[10px] font-bold uppercase tracking-wider">{seasonalEvent.name}</p><p className="text-white/70 text-[10px]">{seasonalEvent.description}</p></div>
              <span className="text-green-400 text-xs font-bold">{seasonalEvent.bonus_multiplier}x Stars!</span>
            </div>
          )}
          {weatherEvent && weatherEvent.has_event && (
            <div className={`rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-3 flex items-center gap-3 ${weatherEvent.event.css_class || ''}`} data-testid="weather-banner">
              <span className="text-xl animate-pulse">⚡</span>
              <div className="flex-1"><p className="text-yellow-300 text-[10px] font-bold uppercase tracking-wider">{weatherEvent.event.name}</p><p className="text-white/70 text-[10px]">{weatherEvent.event.description}</p></div>
              <div className="text-right"><span className="text-yellow-400 text-sm font-bold">+{weatherEvent.event.bonus_stars}</span><p className="text-white/40 text-[9px]">{weatherEvent.minutes_remaining}m left</p></div>
            </div>
          )}
        </div>
      </div>

      {/* ---- QUICK ACCESS ROW ---- */}
      <section className="relative z-10 px-4 py-3" style={{ animation: "section-rise 1s ease-out 0.5s both" }}>
        <div className="max-w-xl mx-auto grid grid-cols-2 gap-2">
          {[
            { to: "/passport", icon: "📖", name: "My Passport", sub: "All stamps & badges", border: "#D97706" },
            { to: "/leaderboard", icon: "🏆", name: "Leaderboard", sub: "Top explorers", border: "#EAB308" },
          ].map(q => (
            <div key={q.to} onClick={() => navigate(q.to)} className="cursor-pointer rounded-xl border-2 p-3 sm:p-4 flex items-center gap-3 hover:scale-[1.02] transition-all" style={{ borderColor: q.border, background: `linear-gradient(135deg, ${q.border}10, rgba(255,255,255,0.02))`, boxShadow: `0 4px 15px ${q.border}10` }} data-testid={`quick-${q.name.toLowerCase().replace(/\s/g, '-')}`}>
              <span className="text-xl sm:text-2xl">{q.icon}</span>
              <div><p className="text-white text-sm font-fredoka font-bold">{q.name}</p><p className="text-white/60 text-xs font-semibold">{q.sub}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- FEATURED ADVENTURE PORTALS (Admin only - Coming Soon for public) ---- */}
      {adventurePaths.length > 0 && isAdminUser(user) && (
        <section className="relative z-10 px-4 py-4" style={{ animation: "section-rise 1s ease-out 0.6s both" }}>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-lg font-fredoka font-bold text-white/90 mb-3 flex items-center gap-2" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}>
              <Sparkles size={18} className="text-orange-400" /> Adventure Portals
              <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-[10px] font-bold uppercase tracking-wide" data-testid="adventures-coming-soon-badge">Coming soon</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {adventurePaths.map(p => (
                <AdventurePortal key={p.id} path={p} onClick={() => toast("🚀 Adventures are coming soon! Hang tight, new worlds are on the way.")} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---- DAILY WORLD CHALLENGES - HIDDEN from homepage (documented in Admin Dashboard) ---- */}
      {/* Daily World Challenges section hidden for launch - managed via Admin > Hidden Sections */}

      {/* ---- TODAY'S EXPLORER JOURNEY: removed from homepage (learning tools live on the Learn page) ---- */}

      {/* ---- MULTIPLAYER EVENT ---- */}
      {multiplayerEvent && multiplayerEvent.event && (
        <section className="relative z-10 px-4 py-4">
          <div className="max-w-3xl mx-auto rounded-2xl bg-indigo-500/8 border-2 border-indigo-500/30 p-4" data-testid="multiplayer-event">
            <h2 className="text-lg font-fredoka font-bold text-white/90 mb-3 flex items-center gap-2" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
              <Users size={18} className="text-indigo-400" /> {multiplayerEvent.event.icon} {multiplayerEvent.event.name}
            </h2>
            <p className="text-white/80 text-xs font-medium mb-3 leading-relaxed">{multiplayerEvent.event.description}</p>
            {multiplayerEvent.event.community_goal && (
              <div className="mb-3">
                <div className="flex justify-between text-[10px] mb-1"><span className="text-white/30">Community</span><span className="text-indigo-300 font-bold">{multiplayerEvent.community_total}/{multiplayerEvent.event.community_goal}</span></div>
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" style={{ width: `${Math.min(100, (multiplayerEvent.community_total / multiplayerEvent.event.community_goal) * 100)}%` }} /></div>
              </div>
            )}
            {multiplayerEvent.leaderboard.length > 0 && (
              <div className="space-y-1">
                {multiplayerEvent.leaderboard.slice(0, 3).map((e, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg bg-white/[0.03] p-1.5">
                    <span className="text-[10px] font-bold" style={{ color: ['#FBBF24', '#94A3B8', '#D97706'][i] }}>#{e.rank}</span>
                    <span className="text-white/50 text-[10px] flex-1">{e.name}</span>
                    <span className="text-yellow-300 text-[10px] font-bold">{e.catches}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ---- DRAWING OF THE DAY ---- */}
      {drawingOfDay && drawingOfDay.drawing && (
        <section className="relative z-10 px-4 py-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-lg font-fredoka font-bold text-white/90 mb-3 flex items-center gap-2" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}>
              <Palette size={18} className="text-pink-400" /> Drawing of the Day
            </h2>
            <div onClick={() => navigate("/art-studio")}
              className="cursor-pointer rounded-2xl border-2 border-pink-500/25 bg-pink-500/5 p-4 hover:border-pink-500/40 transition-all group" data-testid="drawing-of-the-day">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-xl border border-white/10 overflow-hidden bg-[#1a1a2e] flex-shrink-0">
                  {(drawingOfDay.drawing.thumbnail || drawingOfDay.drawing.image_url) ? (
                    <img src={drawingOfDay.drawing.thumbnail || drawingOfDay.drawing.image_url} alt="Drawing of the day" loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Pen size={20} className="text-white/20" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm truncate">{drawingOfDay.drawing.title || drawingOfDay.drawing.prompt || "Featured Art"}</p>
                  <p className="text-white/50 text-xs mt-0.5">by {drawingOfDay.drawing.user_name || "Explorer"}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="flex items-center gap-1 text-xs text-pink-300"><Heart size={11} /> {drawingOfDay.drawing.likes || 0}</span>
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-pink-500/10 text-pink-300 font-bold capitalize">{drawingOfDay.source === "ai-art" ? "AI Art" : "Drawing"}</span>
                  </div>
                </div>
                <ChevronRight size={16} className="text-white/20 group-hover:text-white/40 transition-colors flex-shrink-0" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ---- BOTTOM QUICK LINKS ---- */}
      <section className="relative z-10 px-4 py-3">
        <div className="max-w-3xl mx-auto">
          <div onClick={() => navigate("/trophies")} className="cursor-pointer rounded-xl border-2 p-4 flex items-center gap-3 hover:scale-[1.02] transition-all" style={{ borderColor: "#EAB30850", background: "linear-gradient(135deg, #EAB30808, rgba(255,255,255,0.02))" }} data-testid="achievements-banner">
            <span className="text-2xl">🏆</span>
            <div><p className="text-white text-base font-fredoka font-bold drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">Achievements</p><p className="text-white/80 text-xs font-medium mt-0.5">20 badges to unlock</p></div>
          </div>
        </div>
      </section>

      {/* ---- HOW IT WORKS ---- */}
      <section className="relative z-10 px-4 py-8" data-testid="how-it-works">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-3xl p-6 sm:p-8 border-2 border-cyan-400/25 bg-gradient-to-br from-cyan-500/8 via-blue-500/5 to-teal-500/8" style={{ boxShadow: "0 0 40px rgba(34,211,238,0.08), inset 0 1px 0 rgba(34,211,238,0.1)" }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white font-fredoka font-bold text-lg sm:text-xl flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-cyan-500/20 border border-cyan-400/30">
                  <Rocket size={16} className="text-cyan-300" />
                </div>
                How Isaiah's World Works
              </h2>
              <button onClick={() => navigate("/how-it-works")} className="text-cyan-400 text-xs font-fredoka font-bold hover:text-cyan-300 transition-colors" data-testid="how-it-works-link">
                Learn More <ChevronRight size={12} className="inline" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative">
              {[
                { step: "1", icon: UserPlus, title: "Complete Your Profile", desc: "Set up your grown-up profile in about 2 minutes so you can manage everything from one place.", color: "#06B6D4" },
                { step: "2", icon: Users, title: "Add Your Explorers in Parent Corner", desc: "Have kids to set up? Head to the Parent Corner and add an account for each Explorer, each one gets their own space, progress, and saves. (Solo explorers can skip this.)", color: "#8B5CF6" },
                { step: "3", icon: Sparkles, title: "Start Exploring", desc: "Jump into games, lessons, art projects, and adventures. Everything earns Stars.", color: "#3B82F6" },
                { step: "4", icon: GraduationCap, title: "Learn & Level Up", desc: "Your child grows through play. Track progress, unlock achievements, and keep learning.", color: "#10B981" },
              ].map((s, i) => (
                <div key={i} className="relative text-center p-5 rounded-2xl border border-cyan-400/15 bg-white/8 hover:bg-white/12 hover:border-cyan-400/25 hover:scale-[1.02] transition-all duration-300">
                  {/* Arrow connector between steps (desktop only) */}
                  {i < 2 && (
                    <div className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 items-center justify-center">
                      <ChevronRight size={16} className="text-cyan-400/40" />
                    </div>
                  )}
                  <div className="w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-fredoka font-bold text-sm" style={{ background: `${s.color}20`, border: `2px solid ${s.color}40`, boxShadow: `0 0 15px ${s.color}15` }}>
                    {s.step}
                  </div>
                  <div className="w-12 h-12 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ background: `${s.color}15` }}>
                    <s.icon size={22} style={{ color: s.color }} />
                  </div>
                  <h3 className="text-white font-fredoka font-bold text-base mb-1.5 drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">{s.title}</h3>
                  <p className="text-white/80 text-[13px] leading-relaxed font-medium font-nunito">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---- TESTIMONIALS CAROUSEL ---- */}
      <TestimonialsCarousel navigate={navigate} />

      {/* ---- PARENT TRUST ---- */}
      <section className="relative z-10 px-4 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl border-2 border-green-500/25 bg-green-500/5 p-5" data-testid="parent-trust">
            <div className="flex items-center gap-2 mb-3"><Shield size={18} className="text-green-400" /><h3 className="text-white text-base font-fredoka font-bold">Safe & Purposeful</h3></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { icon: Shield, label: "Kid-safe AI", sub: "Content filtered" },
                { icon: BookOpen, label: "Real learning", sub: "Standards aligned" },
                { icon: Users, label: "Parent tools", sub: "Full visibility" },
              ].map((t, i) => (
                <div key={i} className="text-center"><t.icon size={22} className="mx-auto text-white/75 mb-1.5" /><p className="text-white text-base font-fredoka font-bold drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">{t.label}</p><p className="text-white/80 text-xs font-medium mt-0.5">{t.sub}</p></div>
              ))}
            </div>
            <div className="mt-3 text-center">
              <button onClick={() => navigate("/parent-corner")} className="text-xs text-white/45 hover:text-white/65 font-bold transition-colors" data-testid="parent-corner-link">Parent Corner →</button>
            </div>
          </div>
        </div>
      </section>

      {/* ---- FINAL CTA ---- */}
      {!user && (
        <section className="relative z-10 px-4 py-8" data-testid="final-cta">
          <div className="max-w-2xl mx-auto">
            <div className="rounded-3xl p-8 sm:p-10 text-center border-2 border-purple-400/20 bg-gradient-to-br from-purple-500/10 via-pink-500/5 to-blue-500/10" style={{ boxShadow: "0 0 60px rgba(139,92,246,0.1)" }}>
              <Sparkles size={32} className="mx-auto text-purple-300 mb-4" />
              <h2 className="text-2xl sm:text-3xl font-fredoka font-bold text-white mb-3" style={{ textShadow: "0 2px 15px rgba(0,0,0,0.3)" }}>
                {finalCta.variantValue || "Ready to Transform Screen Time?"}
              </h2>
              <p className="text-white/55 text-sm font-nunito max-w-md mx-auto mb-6 leading-relaxed">
                Join 5,000+ families who turned screen time into learning time. AI-powered, kid-safe, and endlessly fun.
              </p>
              <button onClick={() => { finalCta.trackConversion(); navigate("/signup"); }} className="inline-flex items-center gap-2 px-10 py-4 rounded-full text-white font-fredoka font-bold text-base transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(139,92,246,0.4)]" style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6, #EC4899)", boxShadow: "0 6px 25px rgba(139,92,246,0.35)" }} data-testid="final-cta-btn">
                <Rocket size={18} /> Start Your Free Adventure
              </button>
              <p className="text-white/30 text-xs font-nunito mt-4">Free Basic Account Forever · No credit card required.</p>
            </div>
          </div>
        </section>
      )}

      <section className="relative z-10 px-4 py-1">
        <div className="max-w-3xl mx-auto text-center">
          <button onClick={() => navigate("/admin/themes")} className="text-[10px] text-white/15 hover:text-white/30 transition-colors" data-testid="admin-themes-link">Change Sky Theme</button>
        </div>
      </section>

      <div className="h-16 md:h-8" />
      <PortalTransition active={!!portalFx} color={portalFx || "#3B82F6"} onDone={() => setPortalFx(null)} />
      {showGate && <AuthGate onClose={() => setShowGate(false)} />}
      {premiumModal && (
        <PremiumUpgradeModal
          featureName={premiumModal}
          onClose={() => setPremiumModal(null)}
        />
      )}
    </div>
  );
};

export default HomePage;

/**
 * GuestRoamPanel, bottom-of-homepage CTA letting un-signed-up visitors
 * browse the site for 5 minutes without an account. Renders different
 * copy when a roam timer is already active (countdown + "extend"
 * disabled, they have to sign up to extend).
 */
function GuestRoamPanel({ onStart }) {
  const [remaining, setRemaining] = React.useState(guestRoamRemainingMs());

  React.useEffect(() => {
    const id = setInterval(() => setRemaining(guestRoamRemainingMs()), 1000);
    return () => clearInterval(id);
  }, []);

  const active = isGuestRoamActive();
  const mm = Math.floor(remaining / 60000);
  const ss = Math.floor((remaining % 60000) / 1000).toString().padStart(2, "0");

  if (active) {
    return (
      <div className="mb-3 inline-flex flex-col items-center gap-1.5" data-testid="guest-roam-active">
        <span className="text-xs text-white/55 font-nunito">
          Roaming as a guest, <span className="font-bold text-amber-300">{mm}:{ss}</span> remaining
        </span>
        <p className="text-[10px] text-white/35 max-w-xs">
          Sign up anytime to keep your progress, earn XP, and join the founder rankings.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-3 flex flex-col items-center gap-2" data-testid="guest-roam-panel">
      <button
        type="button"
        onClick={onStart}
        data-testid="guest-roam-start"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 hover:border-white/30 text-white/80 text-xs font-fredoka font-bold transition-all"
      >
        Roam around as a guest for 5 minutes
      </button>
      <p className="text-[10px] text-white/30 max-w-xs">
        No account needed for a quick peek. Sign up later to save your progress &amp; keep your founder rank.
      </p>
    </div>
  );
}
